import { TableTextFilter } from "components/TableTextFilter/TableTextFilter";
import { setScrollBehavior } from "features/scrollPageBehaviorProvider/scrollPageBehaviorProvider";
import { _ } from "i18n";
import type { DiscoverTeamsRowResponse } from "libs/apiClient";
import client from "libs/client";
import { useEffect, useState } from "react";
import DatagridV2, { type SimpleColumn } from "ui-kit/src/Datagrid/DatagridV2";
import {DiscoverBreadcrumbs} from "features/discover/DiscoverBreadcrumbs";
import SelectComponent from "ui-kit/src/SelectComponent/SelectComponent";
import {AgeCategoryOptions} from "features/discover/types";
import {createSortArrows} from "features/discover/functions";

const entityTypeOptions = [{
	label: 'Discover',
	value: 'Discover',
}, {
	label: 'Partner',
	value: 'Partner',
}];

const defaultEntityType = {
	label: "Discover",
	value: "Discover",
};

export function DiscoverTeamsPage() {
    const [pagination, setPagination] = useState<{ pageIndex: number; pageSize: number }>({
        pageIndex: 0,
        pageSize: 15,
    });

    const [filters, setFilters] = useState<{ [key: string]: string | number | undefined }>({entityType: defaultEntityType.value});
    const [filterValues, setFilterValues] = useState<{ [key: string]: string | undefined }>({entityType: defaultEntityType.value});
    const [sort, setSort] = useState<{ field: string, direction: string }>();

    useEffect(() => { setScrollBehavior() }, [])

    const { data, isLoading } = client.getDiscoverTeams.useQuery({
        page: pagination.pageIndex + 1, limit: pagination.pageSize, filters: [
            ...(filters?.id ? [{
                field: "id",
                type: "search",
                value: filters.id,
            }] : []),
            ...(filters?.club ? [{
                field: "club",
                type: "search",
                value: filters.club,
            }] : []),
            ...(filters?.name ? [{
                field: "name",
                type: "search",
                value: filters.name,
            }] : []),
            ...(filters?.entityType ? [{
                field: "entityType",
                type: "exact",
                value: filters.entityType,
            }] : []),
            ...(filters?.country ? [{
                field: "country",
                type: "exact",
                value: filters.country,
            }] : []),
            ...(filters?.city ? [{
                field: "city",
                type: "search",
                value: filters.city,
            }] : []),
            ...(filters?.ageCategory ? [{
                field: "ageCategory",
                type: "exact",
                value: filters.ageCategory,
            }] : []),
        ],
        order: sort
    })

	const { data: countries, isLoading: isCountriesLoading } = client.getDiscoverCountries.useQuery({})

    const handleSetFilter = (key: string, value?: number | string,) => {
        setFilters({ ...filters, [key]: value });
        setPagination({ ...pagination, pageIndex: 0 });
    }

    const handleClearFilter = (key: string) => {
        setFilters({ ...filters, [key]: undefined })
    }

	const handleSort = (newSort?: {field: string, direction: string}) => {
		setSort(newSort);
	}

    const generateColumnHeader = (columnName: string, label: string, isNumber?: boolean, className?: string) => () => (
        <div className={`tableHeader ${className}`}>
            <TableTextFilter
                value={filterValues[columnName] || ""}
                onChange={(value) => setFilterValues({ ...filterValues, [columnName]: value })}
                number={isNumber}
                clear={() => {
                    handleClearFilter(columnName);
                    setFilterValues({ ...filterValues, [columnName]: undefined })
                }}
                onSubmit={(value: string) => handleSetFilter(columnName, value)}
                placeholder={label}
            />
            {createSortArrows(columnName, sort, handleSort)}
        </div>
    );

    const simpleColumns: SimpleColumn<DiscoverTeamsRowResponse>[] = [
		{
			id: "id",
			key: "id",
			header: generateColumnHeader("id", "ID", true, "tableHeader--id"),
		},
		{
			id: "entityType",
			key: "entityType",
			header: () => <div className="tableHeader tableHeader--select">
				<SelectComponent
					value={entityTypeOptions.find((o) => o.value === filters?.entityType)}
					handleChange={(value) => handleSetFilter("entityType", value?.value)}
					placeholder="Type"
					items={entityTypeOptions}
					isCleareable={false}
				/>
			</div>,
			cell: (value) => <div className="thinCell">{value}</div>,
		},
		{
			id: "name",
			key: "name",
			header: generateColumnHeader("name", "Name"),
		},
		{
			id: "ageCategory",
			key: "ageCategory",
			header: () => <div className="tableHeader tableHeader--select">
				<SelectComponent
					value={AgeCategoryOptions.find((o) => o.value === filters?.ageCategory)}
					handleChange={(value) => handleSetFilter("ageCategory", value?.value)}
					placeholder="Age Category"
					items={AgeCategoryOptions}
				/>
			</div>,
			cell: (value) => <div className="thinCell">{value}</div>,
		},
		{
			id: "clubId",
			key: "clubId",
			header: generateColumnHeader("club", "Club"),
			cell: (value, row) => value ? `${row.clubName} (${row.clubId})` : '',
		},
		{
			id: "country",
			key: "country",
			header: () => <div className="tableHeader tableHeader--select">
				<SelectComponent
					value={(countries?.data || []).find((o) => o.value === filters?.country)}
					handleChange={(value) => handleSetFilter("country", value?.value)}
					placeholder="Country"
					items={countries?.data || []}
					isLoading={isCountriesLoading}
				/>
				{createSortArrows("country", sort, handleSort)}
			</div>,
			cell: (value) => <div className="thinCell">{value}</div>,
		},
        {
            id: "region",
            key: "region",
            header: "Region",
			cell: (value) => <div className="thinCell">{value}</div>,
        },
		{
			id: "city",
			key: "city",
			header: generateColumnHeader("city", "City"),
			cell: (value) => <div className="thinCell">{value}</div>,
		},
		{
			id: "usersCount",
			key: "usersCount",
			header: () => <div className="tableHeader">Users</div>,
			cell: (value, row) => <div className="thinCell">{row.entityType === "Discover" ? value : ''}</div>,
		},
    ]

    return (
		<div className="discover-container discover-container--wide">
			<div className="pageHeading">
				<DiscoverBreadcrumbs items={[
					{
						label: 'Teams',
					},
				]}/>
			</div>
			<div className="discover-card discover-card--for-table">
				<DatagridV2
					getRowLink={(row) => `/discover/team/${row.id}`}
					isLoading={isLoading}
					simpleColumns={simpleColumns}
					data={data?.data.items ?? []}
					pageCount={data?.data?.pages ?? 1}
					pagination={pagination}
					setPagination={setPagination}
					noResults={_("datatables.zero_records")}
				/>
			</div>
		</div>
	)
}
